import React, { useEffect, useMemo, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { fmtEUR } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import { Star, Sparkles, Wand2, Check, ExternalLink, ChevronRight, ChevronLeft, Truck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const CAMPI = ["categorie_fornitura", "modalita_ordine", "sito_web", "note_ordine", "preferito", "consegna_in_cantiere"];

const CatChips = ({ value, categorie, onChange, testid }) => {
  const sel = (value || "").split(",").map((s) => s.trim()).filter(Boolean);
  const has = (c) => sel.some((s) => s.toLowerCase() === c.toLowerCase());
  const toggle = (c) => onChange((has(c) ? sel.filter((s) => s.toLowerCase() !== c.toLowerCase()) : [...sel, c]).join(", "));
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {categorie.map((c) => (
          <button key={c} type="button" onClick={() => toggle(c)} data-testid={`${testid}-cat-${c.replace(/\s+/g, "-")}`}
            className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${has(c) ? "border-emerald-600 bg-emerald-600 text-white" : "border-border hover:bg-muted"}`}>
            {c}
          </button>
        ))}
      </div>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="oppure scrivi le tue, separate da virgola"
        className="h-8 text-xs" data-testid={`${testid}-cat-input`} />
    </div>
  );
};

export default function SupplierPreferences() {
  const { user } = useAuth();
  const canWrite = ["super_admin", "amministrazione", "project_manager", "responsabile_cantiere"].includes(user?.role);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [soloIncompleti, setSoloIncompleti] = useState(false);
  const [cerca, setCerca] = useState("");
  const [edit, setEdit] = useState({});
  const [saving, setSaving] = useState(null);
  const [guida, setGuida] = useState(false);
  const [gi, setGi] = useState(0);
  const [pre, setPre] = useState(null);

  const load = async (opts = {}) => {
    setLoading(true);
    try {
      const r = await api.get("/fornitori/preferenze", { params: { solo_incompleti: opts.soloIncompleti ?? soloIncompleti, cerca: (opts.cerca ?? cerca) || undefined } });
      setD(r.data);
      setEdit({});
    } catch (e) { toast.error(fmtErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const categorie = useMemo(() => [...(d?.categorie || []), ...(d?.categorie_usate || [])], [d]);
  const items = d?.items || [];
  const row = (it) => ({ ...it, ...(edit[it.id] || {}) });
  const dirty = (id) => !!edit[id] && Object.keys(edit[id]).length > 0;
  const set = (id, k, v) => setEdit((p) => ({ ...p, [id]: { ...(p[id] || {}), [k]: v } }));

  const save = async (it) => {
    const body = {};
    CAMPI.forEach((k) => { if (edit[it.id] && k in edit[it.id]) body[k] = edit[it.id][k]; });
    if (!Object.keys(body).length) return;
    setSaving(it.id);
    try {
      const r = await api.put(`/fornitori/${it.id}/preferenze`, body);
      setD((p) => ({ ...p, items: p.items.map((x) => (x.id === it.id ? { ...x, ...body, completo: r.data.completo } : x)) }));
      setEdit((p) => { const n = { ...p }; delete n[it.id]; return n; });
      toast.success("Salvato");
    } catch (e) { toast.error(fmtErr(e)); } finally { setSaving(null); }
  };

  const applicaSuggerimento = (it) => {
    const s = it.suggerimento; if (!s) return;
    CAMPI.forEach((k) => { if (s[k] && !(row(it)[k] || "").trim?.()) set(it.id, k, s[k]); });
    if (s.categorie_fornitura && !(row(it).categorie_fornitura || "").trim()) set(it.id, "categorie_fornitura", s.categorie_fornitura);
  };

  const precompilaTutti = async () => {
    try {
      const r = await api.post("/fornitori/preferenze/precompila", {});
      setPre(r.data);
      await load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const incompleti = items.filter((i) => !i.completo);
  const cur = guida ? incompleti[gi] : null;

  return (
    <div className="space-y-4" data-testid="supplier-preferences">
      <Card className="p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Preferiti e modalità d'ordine</h2>
            <p className="text-sm text-muted-foreground">Indica cosa fornisce ogni fornitore, dove si ordina e come: gli agenti useranno queste informazioni per preparare gli ordini.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {canWrite && <Button variant="outline" onClick={precompilaTutti} data-testid="prefs-precompila-btn"><Wand2 className="mr-2 h-4 w-4" />Precompila catene note</Button>}
            {canWrite && <Button onClick={() => { setGi(0); setGuida(true); }} disabled={!incompleti.length} data-testid="prefs-guida-btn"><Sparkles className="mr-2 h-4 w-4" />Procedura guidata ({incompleti.length})</Button>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Input value={cerca} onChange={(e) => setCerca(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Cerca fornitore…" className="h-9 max-w-xs" data-testid="prefs-search" />
          <Button variant="outline" size="sm" onClick={() => load()} data-testid="prefs-search-btn">Cerca</Button>
          <div className="flex items-center gap-2">
            <Switch checked={soloIncompleti} onCheckedChange={(v) => { setSoloIncompleti(v); load({ soloIncompleti: v }); }} data-testid="prefs-only-incomplete" />
            <Label className="text-sm">Solo da completare</Label>
          </div>
          {d && <div className="flex gap-2 text-xs" data-testid="prefs-kpi">
            <Badge variant="outline">{d.totale} fornitori</Badge>
            <Badge className="bg-amber-500 text-white">{d.n_incompleti} da completare</Badge>
            <Badge className="bg-emerald-600 text-white">{d.n_preferiti} preferiti</Badge>
          </div>}
        </div>
      </Card>

      {loading ? <Card className="p-8 text-center text-sm text-muted-foreground">Caricamento…</Card> : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Fornitore</TableHead>
                <TableHead className="min-w-[260px]">Cosa fornisce</TableHead>
                <TableHead className="min-w-[150px]">Come si ordina</TableHead>
                <TableHead className="min-w-[200px]">Sito / portale</TableHead>
                <TableHead>Consegna</TableHead>
                <TableHead className="text-right">Spesa storica</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it0) => {
                const it = row(it0);
                return (
                  <TableRow key={it.id} data-testid={`prefs-row-${it.id}`} className={!it0.completo ? "bg-amber-50/40 dark:bg-amber-950/10" : ""}>
                    <TableCell>
                      <button type="button" disabled={!canWrite} onClick={() => set(it.id, "preferito", !it.preferito)} data-testid={`prefs-star-${it.id}`} title="Fornitore preferito">
                        <Star className={`h-5 w-5 ${it.preferito ? "fill-amber-400 text-amber-500" : "text-muted-foreground"}`} />
                      </button>
                    </TableCell>
                    <TableCell className="align-top">
                      <div className="font-medium">{it.ragione_sociale}</div>
                      <div className="text-xs text-muted-foreground">{it.tipologia || "—"}{it.n_fatture ? ` · ${it.n_fatture} fatture` : ""}{it.n_ordini ? ` · ${it.n_ordini} ordini` : ""}</div>
                      {it0.suggerimento && !it0.completo && (
                        <button type="button" onClick={() => applicaSuggerimento(it0)} className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline" data-testid={`prefs-suggest-${it.id}`}>
                          <Wand2 className="h-3 w-3" />Usa suggerimento ({it0.suggerimento.fonte})
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      <CatChips value={it.categorie_fornitura} categorie={categorie} onChange={(v) => set(it.id, "categorie_fornitura", v)} testid={`prefs-${it.id}`} />
                    </TableCell>
                    <TableCell className="align-top">
                      <Select value={it.modalita_ordine || ""} onValueChange={(v) => set(it.id, "modalita_ordine", v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`prefs-mod-${it.id}`}><SelectValue placeholder="scegli…" /></SelectTrigger>
                        <SelectContent>{(d?.modalita || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                      </Select>
                      <Textarea value={it.note_ordine || ""} onChange={(e) => set(it.id, "note_ordine", e.target.value)} placeholder="note: codice cliente, minimi, pagamento…" className="mt-2 min-h-[54px] text-xs" data-testid={`prefs-note-${it.id}`} />
                    </TableCell>
                    <TableCell className="align-top">
                      <Input value={it.sito_web || ""} onChange={(e) => set(it.id, "sito_web", e.target.value)} placeholder="https://…" className="h-8 text-xs" data-testid={`prefs-sito-${it.id}`} />
                      {it.sito_web && <a href={it.sito_web} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline"><ExternalLink className="h-3 w-3" />apri</a>}
                    </TableCell>
                    <TableCell className="align-top">
                      <div className="flex items-center gap-2">
                        <Switch checked={!!it.consegna_in_cantiere} onCheckedChange={(v) => set(it.id, "consegna_in_cantiere", v)} data-testid={`prefs-consegna-${it.id}`} />
                        <Truck className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </TableCell>
                    <TableCell className="text-right align-top text-sm">{fmtEUR(it.spesa_storica)}</TableCell>
                    <TableCell className="align-top">
                      {dirty(it.id) ? (
                        <Button size="sm" onClick={() => save(it0)} disabled={saving === it.id} data-testid={`prefs-save-${it.id}`}><Check className="mr-1 h-4 w-4" />Salva</Button>
                      ) : it0.completo ? <Badge variant="outline" className="text-emerald-700">completo</Badge>
                        : <Badge className="bg-amber-500 text-white"><AlertTriangle className="mr-1 h-3 w-3" />da completare</Badge>}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!items.length && <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">Nessun fornitore da mostrare</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={guida} onOpenChange={setGuida}>
        <DialogContent className="max-w-lg" data-testid="prefs-guida-dialog">
          <DialogHeader><DialogTitle>Fornitori da completare {cur ? `(${gi + 1} di ${incompleti.length})` : ""}</DialogTitle></DialogHeader>
          {cur ? (() => { const it = row(cur); return (
            <div className="space-y-4">
              <div>
                <div className="text-base font-semibold">{it.ragione_sociale}</div>
                <div className="text-xs text-muted-foreground">{it.tipologia || "—"} · spesa storica {fmtEUR(it.spesa_storica)}</div>
              </div>
              {cur.suggerimento && <Button variant="outline" size="sm" onClick={() => applicaSuggerimento(cur)} data-testid="prefs-guida-suggest"><Wand2 className="mr-2 h-4 w-4" />Usa suggerimento ({cur.suggerimento.fonte})</Button>}
              <div className="space-y-1"><Label className="text-xs">Cosa fornisce</Label>
                <CatChips value={it.categorie_fornitura} categorie={categorie} onChange={(v) => set(it.id, "categorie_fornitura", v)} testid="prefs-guida" /></div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1"><Label className="text-xs">Come si ordina</Label>
                  <Select value={it.modalita_ordine || ""} onValueChange={(v) => set(it.id, "modalita_ordine", v)}>
                    <SelectTrigger data-testid="prefs-guida-mod"><SelectValue placeholder="scegli…" /></SelectTrigger>
                    <SelectContent>{(d?.modalita || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1"><Label className="text-xs">Sito / portale ordini</Label>
                  <Input value={it.sito_web || ""} onChange={(e) => set(it.id, "sito_web", e.target.value)} placeholder="https://…" data-testid="prefs-guida-sito" /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Note per ordinare</Label>
                <Textarea value={it.note_ordine || ""} onChange={(e) => set(it.id, "note_ordine", e.target.value)} data-testid="prefs-guida-note" /></div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2"><Switch checked={!!it.preferito} onCheckedChange={(v) => set(it.id, "preferito", v)} data-testid="prefs-guida-pref" /><Label className="text-sm">Preferito</Label></div>
                <div className="flex items-center gap-2"><Switch checked={!!it.consegna_in_cantiere} onCheckedChange={(v) => set(it.id, "consegna_in_cantiere", v)} data-testid="prefs-guida-consegna" /><Label className="text-sm">Consegna in cantiere</Label></div>
              </div>
            </div>
          ); })() : <p className="text-sm text-muted-foreground">Tutti i fornitori sono completi.</p>}
          <DialogFooter className="flex-row justify-between gap-2">
            <Button variant="ghost" onClick={() => setGi((i) => Math.max(0, i - 1))} disabled={gi === 0} data-testid="prefs-guida-prev"><ChevronLeft className="mr-1 h-4 w-4" />Indietro</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => (gi + 1 < incompleti.length ? setGi(gi + 1) : setGuida(false))} data-testid="prefs-guida-skip">Salta</Button>
              <Button onClick={async () => { if (cur) await save(cur); if (gi + 1 < incompleti.length) setGi(gi + 1); else { setGuida(false); load(); } }} data-testid="prefs-guida-save">
                Salva e avanti<ChevronRight className="ml-1 h-4 w-4" /></Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pre} onOpenChange={() => setPre(null)}>
        <DialogContent className="max-w-lg" data-testid="prefs-precompila-dialog">
          <DialogHeader><DialogTitle>Precompilazione eseguita</DialogTitle></DialogHeader>
          <p className="text-sm">{pre?.n_modificati || 0} fornitori aggiornati (solo i campi vuoti). Controlla e correggi quello che non va.</p>
          <div className="max-h-64 space-y-1 overflow-y-auto text-xs">
            {(pre?.modificati || []).map((m) => <div key={m.id} className="rounded border p-2"><b>{m.fornitore}</b> — {m.campi.join(", ")} <span className="text-muted-foreground">({m.fonte})</span></div>)}
          </div>
          <DialogFooter><Button onClick={() => setPre(null)} data-testid="prefs-precompila-ok">Ho capito</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
